/**
 * /policies/shifts/overtime-rules — Session 3c PR 10.
 * Owning agent: Mutaba'aa.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { OvertimeRuleItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

export function ShiftOvertimeRulesPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-shift-overtime-rules", le],
    queryFn: () => session3cApi.listOvertimeRules(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: PolicyColumn<OvertimeRuleItem>[] = [
    {
      headerKey: "policies.shift_overtime.col.type",
      label: "Rule type",
      render: (r) => <span className="font-medium text-xs">{r.rule_type}</span>,
    },
    {
      headerKey: "policies.shift_overtime.col.multiplier",
      label: "Multiplier",
      render: (r) => <span className="font-mono">{r.multiplier.value}×</span>,
      traceableValue: (r) => ({ label: t("policies.shift_overtime.col.multiplier", "Multiplier"), stack: r.multiplier }),
    },
    {
      headerKey: "policies.shift_overtime.col.floor",
      label: "Statutory floor",
      render: (r) => <span className="font-mono text-xs">{r.statutory_floor}×</span>,
    },
  ];

  return (
    <PolicyListPage<OvertimeRuleItem>
      title={t("policies.shift_overtime.title", "Overtime Premium Rules")}
      subtitle={t("policies.shift_overtime.subtitle", "Article 107 default (1.5x) · per-day-type overrides (weekend · holiday). Mutaba'aa vetoes any multiplier below 1.5x.")}
      agent="mutabaa"
      domain="shift_overtime"
      newButtonLabel={t("policies.shift_overtime.cta.new", "New OT Rule")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => r.id}
      suggestions={[
        {
          headline: t("policies.shift_overtime.suggestion.clean_headline", `${items.length} rule${items.length === 1 ? "" : "s"} loaded.`),
          body: t("policies.shift_overtime.suggestion.clean_body", "All multipliers at or above Article 107 statutory floor (1.5x basic hourly rate)."),
          primaryActionLabel: t("policies.shift_overtime.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log"),
          suggestionKey: "shift-overtime-rules-brief",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("policies.shift_overtime.title", "Overtime Premium Rules"),
        window_label: t("policies.shift_overtime.window", "across day types"),
      }}
    />
  );
}
